import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { CustomDateRangePopover } from "../CustomCalendar";
import { DropArrowIcon, DropDownCheckIcon, RightIcon } from "../../assets/icons";
import { Box } from "@mui/material";
import { DateRangeLabel } from "./DateRange";
import PropTypes from "prop-types";
import { getPresetRange } from "../../utils/dateFormatting";
import {
  CheckIcon,
  PillArrow,
  PillContainer,
  PillContent,
  PillText,
} from "../PillDropdown/PillDropdownStyled";

const TimeFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CustomPopover = styled.div`
  position: absolute;
  top: 0;
  right: 110%;
  margin-left: 8px;
  box-shadow: -4px -4px 8px rgba(204, 210, 221, 0.2),
    4px 4px 12px rgba(90, 108, 135, 0.12);
  border-radius: 12px;
  background: white;
  z-index: 999;
  white-space: nowrap;
`;

const MenuWrapper = styled.div`
  background: white;
  box-shadow: -4px -4px 8px rgba(204, 210, 221, 0.2),
    4px 4px 12px rgba(90, 108, 135, 0.12);
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  position: absolute;
  right: 0;
  // padding: 8px;
`;

const MenuItem = styled.div`
  position: relative;
  padding: 8px;
  font-family: 'Manrope', sans-serif;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  color: ${({ selected }) => (selected ? '#0C53BB' : '#424242')};
  background: ${({ selected }) => (selected ? '#F1F6FE' : 'transparent')};
  min-width: 152px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${({ selected }) => (selected ? '#BBDEFB' : '#F5F5F5')};;
  }
`;

const TimeFilterDropdown = ({
  timeFilter,
  dateLabel,
  options,
  handleTimeFilterChange,
  selectedCustomFilter,
  setSelectedCustomFilter,
  customDate,
  setCustomDate,
  customRange,
  setCustomRange,
  appliedRange,
  setAppliedRange,
  setApply,
  onCustomCalendarOpenChange,
  onCustomCalendarCloseWithoutApply,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [custom, setCustom] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    onCustomCalendarOpenChange?.(custom);
  }, [custom, onCustomCalendarOpenChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        if (custom && timeFilter !== "custom") {
          onCustomCalendarCloseWithoutApply?.();
        }
        setShowMenu(false);
        setCustom(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [custom, timeFilter, onCustomCalendarCloseWithoutApply]);

  const getLabel = () => {
    const selection = options.find((item) => item.key === timeFilter);
    return selection?.label;
  };

  const onClose = () => {
    setShowMenu(false);
    setCustom(false);
  };

  const handleSelectSidemenu = (selectedOption) => {
    setSelectedCustomFilter(selectedOption);

    if (selectedOption === "date_range" || selectedOption === "custom_date") {
      return;
    }

    const [start, end] = getPresetRange(selectedOption);
    if (start && end) {
      setAppliedRange({ startDate: start, endDate: end });
    }
  };

  const handleDateRangeClick = () => {
    setShowMenu(true);
    setCustom(true);
    setApply(false);
    handleSelectSidemenu(dateLabel?.sideMenuSelection);
  };

  const hasDateRange = dateLabel?.startDate || dateLabel?.endDate;

  return (
    <TimeFilterWrapper>
      {timeFilter === "custom" && hasDateRange && (
        <Box>
          <DateRangeLabel
            startDate={dateLabel?.startDate}
            endDate={dateLabel?.endDate}
            handleClick={handleDateRangeClick}
          />
        </Box>
      )}
      <DropdownWrapper ref={dropdownRef}>
        <PillContainer 
          data-testid="month-dropdown"
          onClick={() => {
            setShowMenu((prev) => !prev);
          }}
        >
          <PillContent>
            <PillText> {getLabel()}</PillText>
            <PillArrow isOpen={showMenu}>
              <DropArrowIcon />
            </PillArrow>
          </PillContent>
        </PillContainer>

        {showMenu && (
          <MenuWrapper>
            {options.map((item) => {
              const isCustom = item.key === "custom";
              const isSelected = item.key === timeFilter;
              return (
                <MenuItem
                  key={item.key}
                  selected={isSelected}
                  onClick={() => {
                    if (isCustom) {
                      if (!custom) {
                        setApply(false);
                      }
                      setCustom(!custom);
                    } else {
                      handleTimeFilterChange(item.key);
                      onClose();
                    }
                  }}
                >
                  {item.label}{" "}
                  {isSelected && <CheckIcon><DropDownCheckIcon /></CheckIcon>}
                  {!isSelected && item.label === "Custom" && custom && <RightIcon />}
                  {isCustom && custom && (
                    <CustomPopover onClick={(e) => e.stopPropagation()}>
                      <CustomDateRangePopover
                        handleSelectSidemenu={handleSelectSidemenu}
                        selectedCustomFilter={selectedCustomFilter}
                        customDate={customDate}
                        setCustomDate={setCustomDate}
                        customRange={customRange}
                        setCustomRange={setCustomRange}
                        appliedRange={appliedRange}
                        onApply={handleTimeFilterChange}
                        setApply={setApply}
                        onClose={onClose}
                      />
                    </CustomPopover>
                  )}
                </MenuItem>
              );
            })}
          </MenuWrapper>
        )}
      </DropdownWrapper>
    </TimeFilterWrapper>
  );
};

TimeFilterDropdown.propTypes = {
  timeFilter: PropTypes.string,
  dateLabel: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    sideMenuSelection: PropTypes.string,
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  handleTimeFilterChange: PropTypes.func,
  selectedCustomFilter: PropTypes.string,
  setSelectedCustomFilter: PropTypes.func,
  customDate: PropTypes.object, // assuming dayjs
  setCustomDate: PropTypes.func,
  customRange: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
  }),
  setCustomRange: PropTypes.func,
  appliedRange: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
  }),
  setAppliedRange: PropTypes.func,
  setApply: PropTypes.func,
  onCustomCalendarOpenChange: PropTypes.func,
  onCustomCalendarCloseWithoutApply: PropTypes.func,
};

export default TimeFilterDropdown;
